import { useEffect, useState, useCallback } from 'react';
import { format, isToday, isYesterday } from 'date-fns';
import { getAuditLogByOrgID } from '../../util/util';
import { TruncatedText } from './ui/TooltipCard';

const PAGE_SIZE = 50;

const TABLE_LABELS = {
  shows: 'show',
  venues: 'venue',
  organization: 'organization',
  venue_alterations: 'venue',
};

const ACTION_LABELS = {
  INSERT: 'Created',
  UPDATE: 'Edited',
  DELETE: 'Deleted',
};

const FIELD_LABELS = {
  show_title: 'Title',
  display_title: 'Display title',
  artist_names: 'Artists',
  long_description: 'Description',
  start_date: 'Start date',
  end_date: 'End date',
  opening_date: 'Opening date',
  opening_start_time: 'Opening start',
  opening_end_time: 'Opening end',
  link_out: 'Link',
  link_out_text: 'Link text',
  image_urls: 'Images',
  preview_pdf_path: 'Preview PDF',
  is_archived: 'Hidden',
  merged_into: 'Merged into',
  venue_id: 'Venue',
  original_venue_id: 'Original venue',
  venue_name: 'Name',
  org_name: 'Name',
  address1: 'Address',
  address2: 'Address line 2',
  instagram_username: 'Instagram',
  working_days: 'Working days',
  working_hours_open: 'Opens',
  working_hours_close: 'Closes',
  is_closed: 'Closed',
  contact_email: 'Email',
  from_id: 'From venue',
  to_id: 'Into venue',
};

const HIDDEN_FIELDS = new Set([
  'short_description', 'artist_count', 'metro_area', 'latitude', 'longitude', 'locked', 'created_at',
]);

const INLINE_MAX_LENGTH = 60;
const DIFF_CONTEXT = 24;

function fieldLabel(field) {
  if (FIELD_LABELS[field]) return FIELD_LABELS[field];
  const words = field.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function formatValue(value) {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.length ? value.join(', ') : null;
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function countLabel(n, noun) {
  return `${n} ${noun}${n === 1 ? '' : 's'}`;
}

/** Trim two long strings to the part that differs, with a little context either side. */
function diffSnippets(oldText, newText) {
  let start = 0;
  while (start < oldText.length && start < newText.length && oldText[start] === newText[start]) start++;
  let end = 0;
  while (
    end < oldText.length - start &&
    end < newText.length - start &&
    oldText[oldText.length - 1 - end] === newText[newText.length - 1 - end]
  ) end++;

  // widen to whole words so the snippet doesn't start or end mid-word
  const from = start - DIFF_CONTEXT <= 0 ? 0 : oldText.lastIndexOf(' ', start - DIFF_CONTEXT) + 1;
  const cut = text => {
    const nextSpace = text.indexOf(' ', text.length - end + DIFF_CONTEXT);
    const to = nextSpace === -1 ? text.length : nextSpace;
    return `${from > 0 ? '…' : ''}${text.slice(from, to)}${to < text.length ? '…' : ''}`;
  };
  return [cut(oldText), cut(newText)];
}

/**
 * How one changed field reads in the list: either a single summary (`text`)
 * or an old/new pair.
 */
function describeChange(field, oldValue, newValue) {
  if (field === 'image_urls') {
    const oldUrls = oldValue ?? [];
    const newUrls = newValue ?? [];
    const sameSet = oldUrls.length === newUrls.length && oldUrls.every(url => newUrls.includes(url));
    if (sameSet) return { text: 'Reordered' };
    return { old: countLabel(oldUrls.length, 'image'), new: countLabel(newUrls.length, 'image') };
  }
  if (field === 'preview_pdf_path') {
    if (!oldValue) return { text: 'Added' };
    if (!newValue) return { text: 'Removed' };
    return { text: 'Replaced' };
  }

  const oldText = formatValue(oldValue);
  const newText = formatValue(newValue);
  if (oldText && newText && oldText.length + newText.length > INLINE_MAX_LENGTH) {
    const [oldSnippet, newSnippet] = diffSnippets(oldText, newText);
    return { old: oldSnippet, new: newSnippet };
  }
  return { old: oldText, new: newText };
}

function ChangeValue({ text, isOld }) {
  if (text === null) return <span className="italic text-slate-400">empty</span>;
  return <span className={isOld ? 'text-slate-400 line-through' : 'text-slate-700'}>{text}</span>;
}

function ChangeLine({ field, oldValue, newValue }) {
  const change = describeChange(field, oldValue, newValue);
  const isStacked = !change.text && (change.old?.length ?? 0) + (change.new?.length ?? 0) > INLINE_MAX_LENGTH;
  return (
    <div className="flex gap-2 min-w-0">
      <span className="w-28 shrink-0 truncate font-medium text-slate-700">{fieldLabel(field)}</span>
      {change.text ? (
        <span className="min-w-0 truncate text-slate-700">{change.text}</span>
      ) : isStacked ? (
        <div className="min-w-0 flex-1">
          <div className="truncate"><ChangeValue text={change.old} isOld /></div>
          <div className="truncate"><ChangeValue text={change.new} /></div>
        </div>
      ) : (
        <span className="min-w-0 truncate">
          <ChangeValue text={change.old} isOld />
          <span className="text-slate-400">{' → '}</span>
          <ChangeValue text={change.new} />
        </span>
      )}
    </div>
  );
}

function ChangeSummary({ entry }) {
  const fields = Object.keys(entry.changes ?? {}).filter(field => !HIDDEN_FIELDS.has(field));

  if (entry.table_name === 'venue_alterations') {
    const row = entry.action === 'DELETE' ? 'old' : 'new';
    const fromId = entry.changes?.from_id?.[row];
    const toId = entry.changes?.to_id?.[row];
    return (
      <span className="text-slate-400">
        {entry.action === 'DELETE' ? 'Unmerged' : 'Merged'} venue #{fromId} into #{toId}
      </span>
    );
  }

  if (entry.action !== 'UPDATE') {
    return <span className="text-slate-400">{countLabel(fields.length, 'field')}</span>;
  }

  return (
    <div className="space-y-1">
      {fields.map(field => (
        <ChangeLine
          key={field}
          field={field}
          oldValue={entry.changes[field].old}
          newValue={entry.changes[field].new}
        />
      ))}
    </div>
  );
}

function ItemLabel({ entry }) {
  const label = entry.table_name === 'venue_alterations'
    ? 'Venue combine'
    : entry.record_label ?? `#${entry.record_id}`;

  if (entry.table_name === 'shows' && entry.action !== 'DELETE') {
    return (
      <TruncatedText>
        <a
          href={`/show/${entry.record_id}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-secondary-red hover:underline cursor-pointer"
        >
          {label}
        </a>
      </TruncatedText>
    );
  }
  return <TruncatedText className="text-slate-700">{label}</TruncatedText>;
}

function ActivityTable({ rows }) {
  return (
    <div className="overflow-x-auto">
      <table className="table table-zebra table-fixed w-full">
        <thead>
          <tr>
            <th className="w-[10%] truncate">Time</th>
            <th className="w-[22%] truncate">Item</th>
            <th className="w-[12%] truncate">Action</th>
            <th className="w-[18%] truncate">By</th>
            <th className="truncate">Changes</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(entry => (
            <tr key={entry.id}>
              <td className="align-top whitespace-nowrap text-sm text-slate-400">
                {format(new Date(entry.occurred_at), 'h:mm a')}
              </td>
              <td className="align-top overflow-visible text-sm">
                <ItemLabel entry={entry} />
              </td>
              <td className="align-top truncate text-sm text-slate-400">
                {ACTION_LABELS[entry.action] ?? entry.action} {TABLE_LABELS[entry.table_name] ?? entry.table_name}
              </td>
              <td className="align-top overflow-visible text-sm text-slate-400">
                <TruncatedText>{entry.actor_email ?? 'Unknown'}</TruncatedText>
              </td>
              <td className="align-top text-sm">
                <ChangeSummary entry={entry} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function dayTitle(date) {
  if (isToday(date)) return 'Today';
  if (isYesterday(date)) return 'Yesterday';
  return format(date, 'MMM d, yyyy');
}

/** Consecutive entries (already newest first) bucketed by calendar day. */
function groupByDay(entries) {
  const groups = [];
  for (const entry of entries) {
    const date = new Date(entry.occurred_at);
    const key = format(date, 'yyyy-MM-dd');
    const last = groups[groups.length - 1];
    if (last?.key === key) {
      last.rows.push(entry);
    } else {
      groups.push({ key, title: dayTitle(date), rows: [entry] });
    }
  }
  return groups;
}

/** Superadmin-only log of who changed what for one gallery, read from the audit_log table. */
function ActivitySection({ effectiveOrgId }) {
  const [entries, setEntries] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setEntries(null);
    setLoadError(null);
    (async () => {
      try {
        const data = await getAuditLogByOrgID(effectiveOrgId, null, PAGE_SIZE);
        if (cancelled) return;
        setEntries(data);
        setHasMore(data.length === PAGE_SIZE);
      } catch (err) {
        console.error('Error fetching activity:', err);
        if (!cancelled) setLoadError("Couldn't load activity.");
      }
    })();
    return () => { cancelled = true; };
  }, [effectiveOrgId]);

  const loadMore = useCallback(async () => {
    if (!entries?.length) return;
    setIsLoadingMore(true);
    try {
      const data = await getAuditLogByOrgID(effectiveOrgId, entries[entries.length - 1].id, PAGE_SIZE);
      setEntries(prev => [...prev, ...data]);
      setHasMore(data.length === PAGE_SIZE);
    } catch (err) {
      console.error('Error fetching more activity:', err);
    } finally {
      setIsLoadingMore(false);
    }
  }, [effectiveOrgId, entries]);

  // e.g. a save that only flipped `locked` has nothing worth showing
  const visibleEntries = (entries ?? []).filter(entry => (
    entry.action !== 'UPDATE' ||
    Object.keys(entry.changes ?? {}).some(field => !HIDDEN_FIELDS.has(field))
  ));

  if (loadError) {
    return <p className="mx-8 mt-8 text-sm text-slate-400">{loadError}</p>;
  }

  if (entries === null) {
    return (
      <div className="mx-8 space-y-8 pb-8">
        <div className="h-8 w-24 bg-slate-200 rounded animate-pulse" />
        <div className="px-6 space-y-3">
          {[0, 1, 2, 3].map(i => (
            <div key={i} className="h-10 bg-slate-100 rounded animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="mx-8 space-y-8 pb-8">
      <h2 className="text-2xl text-secondary-red font-semibold tracking-wide uppercase">Activity</h2>

      {visibleEntries.length === 0 && (
        <p className="text-slate-400 text-sm">No changes recorded for this organization yet.</p>
      )}

      {groupByDay(visibleEntries).map(group => (
        <div key={group.key} className="px-6">
          <h3 className="text-lg font-semibold text-secondary-red pb-4">{group.title}</h3>
          <ActivityTable rows={group.rows} />
        </div>
      ))}

      {hasMore && (
        <div className="flex justify-center">
          <button
            className="btn btn-outline btn-sm focus:outline-none"
            onClick={loadMore}
            disabled={isLoadingMore}
          >
            {isLoadingMore && <span className="loading loading-spinner loading-xs text-secondary-red" />}
            Load more
          </button>
        </div>
      )}
    </div>
  );
}

export default ActivitySection;
